import { z } from "zod";

import { PHONE_E164_REGEX, REGISTER_LIMITS, REGISTER_PASSWORD_REGEX } from "../constants/register.constants";

/** Registration account types. `persona` derives the org silently; `empresa` collects org name + email. */
export const ACCOUNT_TYPES = ["persona", "empresa"] as const;
export type AccountType = (typeof ACCOUNT_TYPES)[number];

// Subset of RegisterTranslations keys used for schema-level error messages.
// Decoupled from the i18n module to keep this file pure (no React imports).
export type RegisterSchemaErrorKey =
  | "error_required"
  | "error_email"
  | "error_name_length"
  | "error_org_name_length"
  | "error_password_min"
  | "error_password_number"
  | "error_password_symbol"
  | "error_phone_format"
  | "error_country";

export type RegisterErrorTranslator = (key: RegisterSchemaErrorKey) => string;

/**
 * Build the register form Zod schema with translated error messages.
 *
 * Schema instance is locked at form mount. If the user changes language mid-flow, remount the form (e.g.
 * `<RegisterForm key={activeLanguage} />`) so the resolver picks up new messages.
 */
export function createRegisterSchema(t: RegisterErrorTranslator) {
  const userSchema = z.object({
    name: z
      .string()
      .trim()
      .min(REGISTER_LIMITS.NAME_MIN, t("error_name_length"))
      .max(REGISTER_LIMITS.NAME_MAX, t("error_name_length")),
    email: z.string().trim().min(1, t("error_required")).email(t("error_email")),
    password: z
      .string()
      .min(REGISTER_LIMITS.PASSWORD_MIN, t("error_password_min"))
      .refine((v) => REGISTER_PASSWORD_REGEX.HAS_NUMBER.test(v), t("error_password_number"))
      .refine((v) => REGISTER_PASSWORD_REGEX.HAS_SYMBOL.test(v), t("error_password_symbol")),
    country: z.string().min(2, t("error_country")).max(2, t("error_country")),
    // ColocaPhoneField stores the Maskito-formatted value with spaces and
    // country-specific separators (e.g. BR "+55 48 98878-5792"). Strip every
    // non-digit-non-plus character before validating; backend expects digits
    // only (no leading '+'), so the transform also strips the '+'.
    phone: z
      .string()
      .trim()
      .refine((v) => PHONE_E164_REGEX.test(v.replace(/[^\d+]/g, "")), t("error_phone_format"))
      .transform((v) => v.replace(/\D/g, "")),
  });

  // Org name/email are only collected for the `empresa` account type; for
  // `persona` they are derived from the person at submit time. Base validation
  // keeps them as plain strings (empty allowed); the conditional rules below
  // enforce them only when the account type is `empresa`. Org phone/country are
  // no longer form fields — they always mirror the person's phone/country.
  const orgSchema = z.object({
    name: z.string().trim().max(REGISTER_LIMITS.ORG_NAME_MAX, t("error_org_name_length")),
    email: z.string().trim(),
  });

  return z
    .object({
      accountType: z.enum(ACCOUNT_TYPES),
      user: userSchema,
      organization: orgSchema,
    })
    .superRefine((data, ctx) => {
      if (data.accountType !== "empresa") return;

      const orgName = data.organization.name.trim();
      if (orgName.length < REGISTER_LIMITS.ORG_NAME_MIN) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["organization", "name"],
          message: t("error_org_name_length"),
        });
      }

      const orgEmail = data.organization.email.trim();
      if (!orgEmail) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["organization", "email"],
          message: t("error_required"),
        });
      } else if (!z.string().email().safeParse(orgEmail).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["organization", "email"],
          message: t("error_email"),
        });
      }
    });
}

export type RegisterSchema = ReturnType<typeof createRegisterSchema>;
export type RegisterFormValues = z.infer<RegisterSchema>;
